import { useState, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { ApiError } from '../services/api';
import { Icon } from './Icon';
import { PageHeading } from '../design-system/components';

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return <PageHeading className="page-title row" title={title} description={description} actions={actions} />;
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <span role="status" className="row" style={{ gap: 8 }}>
      <span className="spinner" aria-hidden="true" />
      <span className="muted">{label}…</span>
    </span>
  );
}

export function Loading() {
  return (
    <div className="loading-layout" aria-busy="true" role="status" aria-label="Loading content">
      <span className="sr-only">Loading content…</span>
      {[0, 1, 2].map((row) => (
        <div className="loading-row" key={row} aria-hidden="true">
          <div className="skeleton" />
          <div className="loading-row-body">
            <div className="skeleton" />
            <div className="skeleton" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ErrorBox({ error, retry }: { error: ApiError | Error | string; retry?: () => void }) {
  const message = typeof error === 'string' ? error : error.message;
  return (
    <div className="alert error" role="alert">
      <Icon name="alert" />
      <span className="error-copy">{message}</span>
      {retry && (
        <button className="btn sm" onClick={retry}>
          <Icon name="refresh" />
          Try again
        </button>
      )}
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <Icon name="book" />
      <h3>{title}</h3>
      {children && <div className="empty-copy">{children}</div>}
    </div>
  );
}

export function VisibilityBadge({ visibility }: { visibility: 'public' | 'private' }) {
  return (
    <span className={`badge ${visibility}`}>
      <Icon name={visibility === 'public' ? 'globe' : 'lock'} />
      {visibility === 'public' ? 'Public' : 'Private'}
    </span>
  );
}

export function Avatar({ name, large }: { name: string; large?: boolean }) {
  return (
    <span className={`avatar${large ? ' lg' : ''}`} aria-hidden="true">
      {name.slice(0, 1).toUpperCase()}
    </span>
  );
}

export function CopyCommand({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  return (
    <div className="cmd">
      <Icon name="terminal" />
      <code aria-label={label} tabIndex={0}>
        {text}
      </code>
      <button
        className="btn sm"
        aria-label={`${copied ? 'Copied' : 'Copy'} ${label ?? 'command'}`}
        onClick={async () => {
          try {
            setError('');
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          } catch {
            setError('Clipboard unavailable. Select the text and copy it manually.');
          }
        }}
      >
        <Icon name={copied ? 'check' : 'copy'} />
        {copied ? 'Copied' : 'Copy'}
      </button>
      <span className="sr-only" role="status">
        {copied ? 'Copied to clipboard.' : ''}
      </span>
      {error && (
        <span className="hint" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export function Pagination({
  page,
  perPage,
  total,
  onPage,
}: {
  page: number;
  perPage: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const pages = Math.max(1, Math.ceil(total / perPage));
  if (pages <= 1) return null;
  return (
    <nav className="pagination" aria-label="Pagination">
      <button className="btn sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        <Icon name="chevronLeft" />
        Previous
      </button>
      <span className="muted">
        Page {page} of {pages}
      </span>
      <button className="btn sm" disabled={page >= pages} onClick={() => onPage(page + 1)}>
        Next
        <Icon name="chevronRight" />
      </button>
    </nav>
  );
}

export function Tabs({ items }: { items: { to: string; label: string; end?: boolean }[] }) {
  return (
    <nav className="tabs" aria-label="Sections">
      {items.map((item) => (
        <NavLink key={item.to} to={item.to} end={item.end}>
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

export const UserLink = ({ name }: { name: string }) => <Link to={`/user/${name}`}>{name}</Link>;
